import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { Auth } from "aws-amplify";
import "./../../assets/css/navbar.css";
import { HTTPService } from "./../../services/httpService";
import packageJSON from "../../../package.json";
import JobviousLogo from "./../../assets/Jobvious_white_logo_base.svg";

// New config-driven navigation imports
import { SECTION_ORDER } from "../../config/navConfig";
import { getMenuForRole } from "../../config/roleMenus";
import { loadExpansionState, saveExpansionState, getDefaultExpansionState } from "../../util/navStateUtils";
import NavSection from "../navigation/NavSection";

const API = new HTTPService();

class navBar extends Component {
  doSubmit = async () => {
    Auth.signOut()
      .then(() => {
        localStorage.clear();
        this.props.history.push("/login");
      })
      .catch((err) => {
        //console.log(err);
      });
  };

  state = {
    countsReturned: [], // Keep for backward compatibility
    requestCount: 0,
    jobCount: 0,
    scheduledCount: 0,
    inProgressCount: 0,
    pickupCount: 0,
    reviewCount: 0,
    referralCount: 0,
    myWorkCount: 0,
    loading: true,
    error: null,
    isCalendarConnected: false,
    isStripeConnected: false,
    // Section expansion state
    expandedSections: getDefaultExpansionState(),
    // Submenu state (for Automations)
    submenuState: {
      automations: false
    }
  };

  async componentDidMount() {
    // Load saved expansion state
    const savedExpansion = loadExpansionState(this.props.entityId, this.props.userRole);
    this.setState({ expandedSections: savedExpansion });

    // Dealer group users don't need badge counts — skip data loading
    if (this.props.userType === 'dealer_group') {
      this.setState({ loading: false });
    } else {
      try {
        await this.loadOptimizedNavbarData();
      } catch (error) {
        console.error('Optimized navbar loading failed, falling back to legacy method:', error);
        await this.loadNavbarDataLegacy();
      }

      if (this.props.userType === "installer") {
        await this.checkCalendarStatus();
        await this.checkStripeStatus();

        if (this.isTechnician()) {
          await this.loadMyWorkCount();
        }
      }
    }

    window.addEventListener('calendarStatusChanged', this.handleCalendarStatusChange);
    window.addEventListener('refreshNavbarCounts', this.handleRefreshNavbarCounts);
  }

  componentDidUpdate(prevProps) {
    console.log('🔄 Navbar props changed:', {
      oldEntityId: prevProps.entityId,
      newEntityId: this.props.entityId,
      entityIdChanged: prevProps.entityId !== this.props.entityId
    });

    if (prevProps.entityId !== this.props.entityId) {
      console.log('🆔 EntityId changed, should refresh navbar data');
      // Load expansion state for new company
      const savedExpansion = loadExpansionState(this.props.entityId, this.props.userRole);
      this.setState({ expandedSections: savedExpansion });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('calendarStatusChanged', this.handleCalendarStatusChange);
    window.removeEventListener('refreshNavbarCounts', this.handleRefreshNavbarCounts);
  }

  handleCalendarStatusChange = (event) => {
    this.setState({
      isCalendarConnected: event.detail.connected
    });
  };

  handleRefreshNavbarCounts = async () => {
    console.log('🔄 Refreshing navbar counts...');
    try {
      await this.loadOptimizedNavbarData();
    } catch (error) {
      console.error('Failed to refresh navbar counts:', error);
      await this.loadNavbarDataLegacy();
    }
  };

  checkCalendarStatus = async () => {
    try {
      const response = await API.get(`/installerAuth/${this.props.entityId}`);
      if (response.data && response.data[0]) {
        const { authenticated, calendar_id } = response.data[0];
        this.setState({
          isCalendarConnected: authenticated && !!calendar_id
        });
      }
    } catch (error) {
      console.error('Error checking calendar status in navbar:', error);
    }
  };

  checkStripeStatus = async () => {
    try {
      const response = await API.get(`/installers/${this.props.entityId}`);
      if (response.data && response.data[0]) {
        const { stripe_account_id, onboarding_complete } = response.data[0];
        this.setState({
          isStripeConnected: !!stripe_account_id && !!onboarding_complete
        });
      }
    } catch (error) {
      console.error('Error checking Stripe status in navbar:', error);
    }
  };

  loadMyWorkCount = async () => {
    try {
      const response = await API.get(`/requestService/technician/${this.props.userId}`);
      if (response.data && Array.isArray(response.data)) {
        const activeServices = response.data.filter(s => s.service_state !== 'finished');
        this.setState({ myWorkCount: activeServices.length });
      }
    } catch (error) {
      console.error('Error loading My Work count:', error);
    }
  };

  loadOptimizedNavbarData = async () => {
    console.log('🚀 Loading optimized navbar data...');
    const start = performance.now();

    try {
      const navbarRequest = `/navbar-data-cached?entityId=${this.props.entityId}&userId=${this.props.userId}&userType=${this.props.userType}&access=${this.props.access || 'STANDARD'}`;
      const { data: navbarData } = await API.get(navbarRequest);

      const loadTime = Math.round(performance.now() - start);
      console.log(`✅ Navbar data loaded in ${loadTime}ms:`, navbarData);

      this.setState({
        requestCount: navbarData.requestCount || 0,
        jobCount: navbarData.jobCount || 0,
        scheduledCount: navbarData.scheduledCount || 0,
        inProgressCount: navbarData.inProgressCount || 0,
        pickupCount: navbarData.pickupCount || 0,
        reviewCount: navbarData.reviewCount || 0,
        referralCount: navbarData.referralCount || 0,
        authenticated: navbarData.authenticated || false,
        calendar: navbarData.calendar || "",
        isStripeConnected: navbarData.stripeConnected || false,
        loading: false,
        error: null,
        countsReturned: []
      });
    } catch (error) {
      console.error('❌ Optimized navbar loading failed:', error);
      this.setState({ error: error.message, loading: false });
      throw error;
    }
  };

  loadNavbarDataLegacy = async () => {
    console.log('🔄 Loading navbar data using legacy method...');
    const start = performance.now();

    try {
      let countRequest = `/counts/?userId=${this.props.entityId}&userType=${this.props.userType}`;
      const { data: countsReturned } = await API.get(countRequest);
      this.setState({ countsReturned });

      if (this.props.userType === "dealer" && this.props.access !== "DIRECT") {
        let referralRequest = `/referralcounts/?userId=${this.props.userId}&userType=${this.props.userType}`;
        const { data: referralsReturned } = await API.get(referralRequest);
        if (referralsReturned.length > 0) {
          let fullCountsReturned = countsReturned.concat(referralsReturned);
          this.setState({ countsReturned: fullCountsReturned });
        }
      }

      var requestCount = 0;
      var jobCount = 0;
      var scheduledCount = 0;
      var inProgressCount = 0;
      var pickupCount = 0;
      var reviewCount = 0;
      var referralCount = 0;

      for (var i = 0; i < this.state.countsReturned.length; i++) {
        switch (this.state.countsReturned[i].current_state) {
          case "referrals":
            referralCount = referralCount + this.state.countsReturned[i].total;
            break;
          case "new":
            requestCount = requestCount + this.state.countsReturned[i].total;
            break;
          case "answered":
            requestCount = requestCount + this.state.countsReturned[i].total;
            break;
          case "scheduled":
            scheduledCount = scheduledCount + this.state.countsReturned[i].total;
            jobCount = jobCount + this.state.countsReturned[i].total;
            break;
          case "received":
            inProgressCount = inProgressCount + this.state.countsReturned[i].total;
            jobCount = jobCount + this.state.countsReturned[i].total;
            break;
          case "started":
            inProgressCount = inProgressCount + this.state.countsReturned[i].total;
            jobCount = jobCount + this.state.countsReturned[i].total;
            break;
          case "finished":
            pickupCount = pickupCount + this.state.countsReturned[i].total;
            break;
          case "collected":
            if (this.props.userType === "dealer") {
              reviewCount = reviewCount + this.state.countsReturned[i].total;
            }
            break;
          default:
            break;
        }
      }

      this.setState({
        requestCount,
        jobCount,
        scheduledCount,
        inProgressCount,
        pickupCount,
        reviewCount,
        referralCount,
        loading: false,
        error: null
      });

      const loadTime = Math.round(performance.now() - start);
      console.log(`✅ Legacy navbar data loaded in ${loadTime}ms`);
    } catch (error) {
      console.error('❌ Legacy navbar loading also failed:', error);
      this.setState({
        error: error.message,
        loading: false,
        requestCount: 0,
        jobCount: 0,
        scheduledCount: 0,
        inProgressCount: 0,
        pickupCount: 0,
        reviewCount: 0,
        referralCount: 0,
        authenticated: false,
        calendar: ""
      });
    }
  };

  readAuth = async (installerId) => {
    let installerRequest = `/installerAuth/${installerId}`;
    const { data: installer } = await API.get(installerRequest);
    this.setState({
      authenticated: installer[0].authenticated,
      calendar: installer[0].calendar_id,
    });
  };

  navigate = async (url) => {
    window.location.href = url;
  };

  authorizeGoogle = async () => {
    let userSetup = `/request`;
    const { data: creds } = await API.get(userSetup);
    this.navigate(creds);
  };

  refreshNavbarData = () => {
    this.setState({ loading: true });
    this.loadOptimizedNavbarData().catch(() => {
      this.loadNavbarDataLegacy();
    });
  };

  isTechnician = () => {
    return this.props.userRole === 'technician';
  };

  isManagerOrOwner = () => {
    const role = this.props.userRole;
    return !role || role === 'manager' || role === 'owner';
  };

  // Section expansion handlers
  handleToggleSection = (section) => {
    this.setState(prev => {
      const newExpanded = {
        ...prev.expandedSections,
        [section]: !prev.expandedSections[section]
      };
      // Persist to localStorage
      saveExpansionState(this.props.entityId, this.props.userRole, newExpanded);
      return { expandedSections: newExpanded };
    });
  };

  // Submenu toggle handler (for Automations)
  handleSubmenuToggle = (itemId) => {
    this.setState(prev => ({
      submenuState: {
        ...prev.submenuState,
        [itemId]: !prev.submenuState[itemId]
      }
    }));
  };

  // Build badge counts object for NavSection
  getBadgeCounts = () => {
    return {
      leads_actionable: this.state.referralCount,
      requestimates_actionable: this.state.requestCount,
      jobs_actionable: this.state.jobCount,
      scheduled_actionable: this.state.scheduledCount || 0,
      inprogress_actionable: this.state.inProgressCount || 0,
      pickupdeliver_actionable: this.state.pickupCount,
      pickups_actionable: this.state.pickupCount,
      referrals_actionable: this.state.referralCount,
      reviews_actionable: this.state.reviewCount,
      mywork_actionable: this.state.myWorkCount
    };
  };

  // Build warnings object for NavSection
  getWarnings = () => {
    return {
      calendarNotConnected: !this.state.isCalendarConnected,
      stripeNotConnected: !this.state.isStripeConnected
    };
  };

  render() {
    // Loading state
    if (this.state.loading) {
      return (
        <React.Fragment>
          <div className="main-sidebar bg-primary-orange">
            <div className="d-flex align-items-center justify-content-center">
              <img
                src={JobviousLogo}
                alt="Jobvious"
                width="200px"
                className="mb-5 mt-5"
              />
            </div>
            <div className="text-center text-light">
              <small>Loading...</small>
            </div>
          </div>
        </React.Fragment>
      );
    }

    // Error state
    if (this.state.error) {
      return (
        <React.Fragment>
          <div className="main-sidebar bg-primary-orange">
            <div className="d-flex align-items-center justify-content-center">
              <img
                src={JobviousLogo}
                alt="Jobvious"
                width="200px"
                className="mb-5 mt-5"
              />
            </div>
            <div className="text-center text-light">
              <small>Error loading menu</small>
              <button
                className="btn btn-sm btn-light mt-2"
                onClick={this.refreshNavbarData}
              >
                Retry
              </button>
            </div>
          </div>
        </React.Fragment>
      );
    }

    // Get menu for current user role
    const menu = getMenuForRole(
      this.props.userType,
      this.props.userRole,
      this.props.access,
      { technicianCanActionLeads: true } // TODO: Get from company settings
    );

    const badgeCounts = this.getBadgeCounts();
    const warnings = this.getWarnings();

    return (
      <React.Fragment>
        <div className="main-sidebar bg-primary-orange">
          <div className="d-flex align-items-center justify-content-center">
            <img
              src={JobviousLogo}
              alt="Jobvious"
              width="200px"
              className="mb-3 mt-3"
            />
          </div>

          {/* Config-driven navigation sections */}
          {SECTION_ORDER.map(section => {
            const items = menu[section];
            if (!items || items.length === 0) return null;

            return (
              <NavSection
                key={section}
                section={section}
                items={items}
                isExpanded={this.state.expandedSections[section]}
                onToggle={this.handleToggleSection}
                counts={badgeCounts}
                warnings={warnings}
                userType={this.props.userType}
                submenuState={this.state.submenuState}
                onSubmenuToggle={this.handleSubmenuToggle}
              />
            );
          })}

          <div className="mt-5 mb-5 text-center">
            <button
              className="btn btn-success btn-primary-green rounded-pill"
              onClick={this.doSubmit}
            >
              SIGN OUT
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <div className="version text-center text-light">
              <small>
                R:{this.state.requestCount} J:{this.state.jobCount} P:{this.state.pickupCount}
              </small>
            </div>
          )}

          <div className="version text-center text-light">
            v {packageJSON.version}
          </div>
          <div className="version text-center">
            <a
              className="text-light"
              href="https://s3.us-east-2.amazonaws.com/jobvious.digitalassets/Jobvious_Release_Notes.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Release Notes
            </a>
          </div>
          <div className="version text-center">
            <a
              className="text-light"
              href="https://s3.us-east-2.amazonaws.com/jobvious.digitalassets/Jobvious.Privacy.Policy.10.6.21.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </div>
          <div className="version text-center">
            <a
              className="text-light"
              href="https://s3.us-east-2.amazonaws.com/jobvious.digitalassets/Jobvious.Limited.Use.Policy.7.10.24.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Policy
            </a>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(navBar);
